/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('size-scale-modal')) return;

  const SIZE_RE = /(\d+(?:\.\d+)?)L"x(\d+(?:\.\d+)?)H"/;
  const ROOM_W_IN = 160;
  const ROOM_H_IN = 100;
  const FLOOR_IN = 93.333;
  const CENTER_IN = 60;

  const motionOn = () =>
    window.gsap && document.documentElement.classList.contains('motion');

  function parseSize(value) {
    const m = String(value || '').match(SIZE_RE);
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }

  class SizeScaleModal extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.modal = this.querySelector('.modal');
      this.panel = this.querySelector('.modal__panel');
      this.canvasEl = this.querySelector('[data-scale-canvas]');
      this.dimsEl = this.querySelector('[data-scale-dims]');
      this.isOpen = false;

      const activeChip = this.querySelector('[data-scale-size].is-active');
      this.size = (activeChip && parseSize(activeChip.dataset.scaleSize)) || { w: 24, h: 30 };

      this.onKeydown = (e) => {
        if (e.key === 'Escape') this.close();
      };

      this.querySelectorAll('[data-size-scale-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );

      this.querySelectorAll('[data-scale-size]').forEach((chip) =>
        chip.addEventListener('click', () => {
          const value = chip.dataset.scaleSize;

          const radio = document.querySelector(
            `[data-section-id="${this.sectionId}"] [data-option-input][data-option-value="${CSS.escape(value)}"]`
          );
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            const parsed = parseSize(value);
            if (parsed) this.setSize(parsed.w, parsed.h, true);
          }
          this.markChip(value);
        })
      );

      this.onDocClick = (e) => {
        const trigger = e.target.closest('[data-size-scale-open]');
        if (!trigger) return;
        const section = trigger.closest('[data-section-id]');
        if (section && section.getAttribute('data-section-id') !== this.sectionId) return;
        e.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this.onDocClick);

      if (window.AirX) {
        this.onBackdrop = () => {
          if (this.isOpen) this.close();
        };
        window.AirX.on('overlay:backdrop-click', this.onBackdrop);

        this.onVariantChange = (e) => {
          const d = e.detail || {};
          if (d.sectionId !== this.sectionId || !d.variant) return;
          let parsed = null;
          let raw = null;
          (d.variant.options || []).forEach((opt) => {
            if (!parsed) {
              const p = parseSize(opt);
              if (p) {
                parsed = p;
                raw = opt;
              }
            }
          });
          if (parsed) {
            this.setSize(parsed.w, parsed.h, this.isOpen && !d.initial);
            if (raw) this.markChip(raw);
          }
        };
        window.AirX.on('variant:change', this.onVariantChange);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
      if (window.AirX) {
        if (this.onBackdrop) window.AirX.off('overlay:backdrop-click', this.onBackdrop);
        if (this.onVariantChange) window.AirX.off('variant:change', this.onVariantChange);
      }
    }

    markChip(value) {
      this.querySelectorAll('[data-scale-size]').forEach((chip) => {
        const active = chip.dataset.scaleSize === value;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', String(active));
      });
    }

    setSize(w, h, animate) {
      this.size = { w, h };
      if (!this.canvasEl) return;

      const widthPct = (w / ROOM_W_IN) * 100;
      const heightPct = (h / ROOM_H_IN) * 100;
      const topPct = ((FLOOR_IN - CENTER_IN - h / 2) / ROOM_H_IN) * 100;

      const apply = () => {
        this.canvasEl.style.width = widthPct + '%';
        this.canvasEl.style.height = heightPct + '%';
        this.canvasEl.style.top = topPct + '%';
      };

      if (animate && motionOn() && this.isOpen) {
        if (window.Flip) {
          const state = window.Flip.getState(this.canvasEl);
          apply();
          window.Flip.from(state, { duration: 0.55, ease: 'expo.out', scale: true });
        } else {
          apply();
          window.gsap.fromTo(
            this.canvasEl,
            { scale: 0.9, opacity: 0.6, transformOrigin: '50% 50%' },
            { scale: 1, opacity: 1, duration: 0.5, ease: 'expo.out', clearProps: 'transform,opacity' }
          );
        }
      } else {
        apply();
      }

      if (this.dimsEl) {
        const template = this.dataset.dimsTemplate || '[w]″ W × [h]″ H';
        this.dimsEl.textContent = template.replace('[w]', w).replace('[h]', h);
      }
    }

    open(trigger) {
      if (this.isOpen || !this.modal) return;
      this.isOpen = true;
      this.trigger = trigger;
      if (window.gsap && window.Flip && !window.gsap.core.globals().Flip) {
        window.gsap.registerPlugin(window.Flip);
      }
      this.setSize(this.size.w, this.size.h, false);
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this.onKeydown);
      if (window.AirX) {
        window.AirX.overlayOpened('size-scale-' + this.sectionId);
        window.AirX.trapFocus(this.panel, this.querySelector('[data-size-scale-close]'));
      }
      if (motionOn()) {
        window.gsap.fromTo(
          this.canvasEl,
          { opacity: 0, scale: 0.85, transformOrigin: '50% 50%' },
          { opacity: 1, scale: 1, duration: 0.7, ease: 'expo.out', delay: 0.15, clearProps: 'transform,opacity' }
        );
      }
    }

    close() {
      if (!this.isOpen || !this.modal) return;
      this.isOpen = false;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onKeydown);
      if (window.AirX) {
        window.AirX.overlayClosed('size-scale-' + this.sectionId);
        window.AirX.releaseFocus();
      }
    }
  }

  customElements.define('size-scale-modal', SizeScaleModal);
})();
